import { type Descendant, type EditorVersion } from '../../editor/types'
import {
  ChargedPaymentReceived,
  CustomFeePercent,
  Invoice,
  PracticeInvoiceOmission,
} from '../../invoicing/types/invoice'
import { LaborChartingData } from '../../labor/types/laborEntry'
import { PatientAuthorization } from '../authorizations'
import { type SubmitPregnancyClaimArgs } from '../claims'
import { ClaimSnippet, PregnancyClaim } from '../claims/claim'
import { FileDBValue, ServerTimestamp } from '../data'
import { AssessmentDocument } from '../documents/documents'
import {
  BaseInsuranceCoverage,
  CoverageWithMetadata,
  InsuranceCoverage,
  PatientCoverageId,
  PregnancyCoverage,
} from '../insurance'
import { AssessmentPaymentsData, PaymentDue, PaymentReceived } from '../payments'
import { Alert } from '../shared/alert'
import { DBMetadata, UserGroup, WithMetadata, WithPartialMetadata } from '../shared/db'
import { TemplateEditorDraft } from '../templates'
import { PatientNameItem, UserRoleItem } from '../users'
import {
  AssessmentStatus,
  FieldMapValue,
  Log,
  NextAction,
  Note,
  RedFlagRecord,
  WithId,
} from '../utils'
import { AssessmentStage, AssessmentStages } from './shared'

export type PracticeAssessmentStage =
  | 'Inquiry'
  | 'Current'
  | 'Postpartum'
  | 'Gyn'
  | 'Complete'
  | 'Other'
  | 'Discharged'
export type PracticeAssessmentStages = PracticeAssessmentStage[]

export interface FaxRecord {
  sentOn: number
  sentBy: string
  text: any[]
}

export interface ReferenceNumberData {
  referenceNumber?: string
  notes?: string
  updatedOn?: number
  updatedBy?: string
}

export interface CoverageNextActions {
  additionalPlans?: Record<string, NextAction>
  primaryCoverage?: NextAction | null
  secondaryCoverage?: NextAction | null
}

export type StageHistoryOperation = 'add' | 'remove' | 'move'
export interface StageHistoryItem<Stage extends string> {
  operation: StageHistoryOperation
  stages: Stage[]
  on: number
  by: string
  group: UserGroup
  notes?: string
}

export interface MiscarriedHistoryItem {
  miscarried: boolean
  on: number
  by: string
  group: UserGroup
}

export interface AdminAssessmentData {
  // useManualPayments?: boolean
  // manualPayments?: LegacyPaymentsData<any>
  // patientPrm?: number
  authorizations?: Record<string, PatientAuthorization>
  // claims?: Record<string, Claim>
  log?: Log | null
  alerts?: Record<number, string | Alert>
  urgentReason?: string | null
  claimsDrafts?: Record<string, { name: string; data: Partial<SubmitPregnancyClaimArgs> }>
  financialAdj?: number
  balanceTowardFee?: number
  // nextAction?: NextAction | null
  nextActions?: CoverageNextActions | null
  backupNextActions?: CoverageNextActions | null
  gynNextAction?: NextAction | null

  files?: Record<string, FileDBValue>
  // miscarried?: boolean
  patientPrmNotes?: string | null
  authReferenceNumbers?: ReferenceNumberData[]
  claimsReferenceNumbers?: ReferenceNumberData[]
  assessmentNote?: Note | null
  authNote?: Note | null
  claimsNote?: Note | null
  paymentsNote?: Note | null
  redFlags?: RedFlagRecord
  payments?: AssessmentPaymentsData
  faxRecords?: Record<string, FaxRecord[]>
  hasOverduePayment?: boolean
  draft?: Descendant[]
  draftEditorVersion?: EditorVersion
  stages: AssessmentStages
  stageHistory?: StageHistoryItem<AssessmentStage>[]
  // other drafts (clinicals, receipts, etc)
  drafts?: Record<string, Descendant[] | TemplateEditorDraft>
  draftSavedOn?: number
  draftSavedBy?: string
  forceUpdateOn?: number
  unreadPregnancyMessages?: number
}

export interface PracticeAssessmentListProps {
  onInquiryList: boolean
  onAuthList: boolean
  onSendClaimList: boolean
  onGynList: boolean
  onOtherList: boolean
  onCompleteList: boolean
  onDischargedList: boolean
}

export type PracticeAssessmentData = Pick<
  AdminAssessmentData,
  | 'balanceTowardFee'
  | 'files'
  | 'alerts'
  // | 'miscarried'
  | 'assessmentNote'
  // | 'miscarriedUpdatedBy'
  // | 'miscarriedUpdatedByGroup'
  // | 'miscarriedUpdatedOn'
  // | 'miscarriedHistory'
  | 'financialAdj'
  | 'hasOverduePayment'
> &
  Pick<
    AssessmentSnippet,
    | keyof PatientNameItem
    | 'miscarried'
    | 'patientId'
    | 'isInvite'
    | 'assessmentName'
    | 'balanceTowardPrm'
    | 'dob'
    | 'dobString'
    | 'nickname'
    | 'markedCompleteOn'
    | 'isGynProfile'
    | 'signedOnDate'
    | 'email'
    | 'emailLower'
    | 'hasOverduePayment'
    | 'sharedNote'
    | 'sharedPaymentNote'
    | 'phone'
    | 'partnerName'
    | 'unreadThreadMessages'
    | 'hasComplaints'
    | 'planCoverageType'
    | 'planState'
    | 'names'
    | 'isMedicaidPlan'
    | 'clearedOn'
    | 'clearedBy'
    | 'clearedHistory'
    | 'partnerEmail'
    | 'isTransfer'
    | 'nextActionDate'
    | 'insuranceCoverage'
    | 'nextActionText'
    | 'authRefNumbers'
    | 'claimRefNumbers'
    | 'eftCheckRefNumbers'
    | 'stringified'
    | 'archived'
    | 'edd'
    | 'prm'
    | 'deliveredOn'
    | 'status'
    | 'midwifeName'
    | 'createdOn'
  > &
  PracticeAssessmentListProps & {
    midwifeId: string
    sortColor: string
    stages: PracticeAssessmentStages
    stageHistory?: StageHistoryItem<PracticeAssessmentStage>[]
    nextPatientAppointmentDate: number | null
    archivedOn: number | null
    archivedBy: string | null
    archivedByGroup?: UserGroup | null
    archivedHistory?: ArchivedHistoryItem[]
    nextAction?: NextAction | null
    payments: AssessmentPaymentsData
    // delivery: AssessmentSnippet['delivery']
    inLabor?: boolean
    // §0.5.2 denormalized indicators
    hasUnreviewedLabResults?: boolean
    hasOverdueLabs?: boolean
    hasActiveProviderFlags?: boolean
    colorTagsByProvider?: Record<string, string>
  }

export interface AssessmentInvoiceSnippet {
  manuallyAddedBy: string | null
  invoiceId: string
  practiceId: string
  invoiceNumber: number | null
  sentOn: string | null
  sentBy: string | null
  chargedPayments: Record<string, ChargedPaymentReceived>
  invoiceStatus: Invoice['status']
  manualPaymentIds: string[] | null
  omissions: Record<string, PracticeInvoiceOmission>
  customFeePercents: Record<string, CustomFeePercent>
  practiceName: string
  scheduledFor: string | null
  payment: {
    amount: number
    paidOn: string | null
    markedPaidBy: string | null
    paymentType: string | null
  } | null
}

export type AssessmentInvoiceSnippets = Record<string, AssessmentInvoiceSnippet>

export type DeliveryType = 'nsvd' | 'cSection' | 'breech' | 'vacuum' | 'forceps'
export type DeliveryLocation =
  | 'home-birth'
  | 'facility-birth'
  | 'birthing-center-birth'
  | 'undecided'

export interface Delivery {
  deliveredOn: number | null
  location: DeliveryLocation | 'other'
  locationOther?: string
  newbornSex: string
  deliveryTypes?: DeliveryType[]
  newbornFname: string | null
  newbornLname: string | null
  newbornWeight: number | null
  summary?: string
  isTransfer: boolean
  lactationClassCompleted?: boolean
  lactationClassCompletedOn?: number | null
  lactationProblem?: boolean
  lactationProblemOn?: number | null
  lactationProblemNotes?: string | null
  rhogamAdministration?: boolean
  rhogamAdministrationOn?: number | null
}

export interface ClearedHistoryItem {
  cleared: boolean
  on: number
  by: string
  group: UserGroup
  notes?: string
}

export interface AdminAssessmentListProps {
  onInquiryList: boolean
  onAuthList: boolean
  onOtherList: boolean
  onSendClaimList: boolean
  onClaimsList: boolean
  onComplaintsList: boolean
  onCompleteList: boolean
}

export interface AssessmentSnippet
  extends Pick<
    UserRoleItem,
    keyof PatientNameItem | 'email' | 'emailLower' | 'phone' | 'dob' | 'dobString' | 'isInvite'
  > {
  stringified: string
  hasOverduePayment: boolean
  isGynProfile: boolean

  // derived from assessment
  patientId: string
  assessmentName: string
  nickname: string | null
  createdOn: number
  midwifeName: string
  midwifeId: string | null
  planCoverageType: string | null
  planState: string | null
  // isMarketplacePlan: boolean
  // true if Medicaid, false if Marketplace, null if no coverage or not determined
  isMedicaidPlan: boolean | null
  hasComplaints: boolean
  insurerId: string | null
  insuranceCoverage: string
  status: AssessmentStatus
  edd: number
  signedOnDate: number | null
  additionalAuthRefNumbers: ReferenceNumberData[]
  additionalClaimsRefNumbers: ReferenceNumberData[]
  claimRefNumbers: string[]
  authRefNumbers: string[]
  eftCheckRefNumbers: string[]
  claims: Record<string, ClaimSnippet>
  plans?: PopulatedPregnancyPlans
  // TODO: remove this after moving plans are linked
  // patientPlans?: PopulatedInsurancePlans
  signOnReminders: { sentOn: number }[]

  // directly copied from admin data
  stages: AdminAssessmentData['stages']
  onInquiryList: boolean
  onAuthList: boolean
  onOtherList: boolean
  onSendClaimList: boolean
  onClaimsList: boolean
  onComplaintsList: boolean
  onCompleteList: boolean
  insuranceCallInComplete?: boolean
  insuranceCallInRequired?: boolean

  deliveredOn: number | null
  isTransfer?: boolean
  // delivery: WithMetadata<Partial<Delivery>> | null
  assessmentSentOn: number | null
  urgentReason: AdminAssessmentData['urgentReason'] | null
  nextActions?: CoverageNextActions | null
  nextActionsString: string
  gynNextAction?: AdminAssessmentData['gynNextAction'] | null
  isUrgent: boolean

  assignedAdmins: string[]
  // derived from admin data
  markedCompleteOn: number | null
  logSnippet: Log | null
  archived: boolean
  nextActionText: string
  nextActionDate: number
  partnerName: string
  partnerEmail: string
  redFlagText: string
  inquiryRank: number
  // invoiceIds: Array<string>
  duePayments: WithId<PaymentDue>[]
  receivedPayments: WithId<PaymentReceived>[]
  prm: number | null
  balanceTowardPrm: number | null
  unreadThreadMessages: number
  unreadPatientMessages: number
  unreadPregnancyMessages: number

  // cleared for coverage
  clearedOn: number | null
  clearedBy: string | null
  clearedHistory?: ClearedHistoryItem[]

  signedOnByPractice: number | null
  signedOnByPracticeUser: string | null

  sharedNote: Note | null
  sharedPaymentNote: Note | null

  forceUpdateOn: number
  // searchEmbedding?: number[]
  miscarried: boolean

  // TODO: remove
  // clearedOnInitializedOn?: number

  inLabor?: boolean

  // §0.5.2 Denormalized indicator fields — updated by Cloud Functions
  /** True when ≥1 LabResult with flagStatus !== 'normal' and acknowledgedAt absent */
  hasUnreviewedLabResults?: boolean
  /** True when ≥1 LabResult with no resultDate and createdOn > 7 days ago */
  hasOverdueLabs?: boolean
  /** True when ≥1 active (non-removed) provider flag exists for this patient */
  hasActiveProviderFlags?: boolean
  /** §0.5.8 Color dot tag — keyed by provider userId, value is ColorTagColor */
  colorTagsByProvider?: Record<string, string>
}
export type AssessmentInterface = {
  [K in keyof AssessmentSnippet & keyof PracticeAssessmentData]:
    | AssessmentSnippet[K]
    | PracticeAssessmentData[K]
}

export type ClaimInterface = Pick<
  {
    [K in keyof PregnancyClaim & keyof AssessmentSnippet]: PregnancyClaim[K] | AssessmentSnippet[K]
  },
  | keyof PatientNameItem
  | 'authRefNumbers'
  | 'claimRefNumbers'
  | 'eftCheckRefNumbers'
  | 'nextActionsString'
  | 'dobString'
  | 'emailLower'
  | 'phone'
  | 'names'
>

export interface ArchivedHistoryItem {
  archived: boolean
  on: number
  by: string
  group: UserGroup
  notes?: string
}

export interface ScheduledCall {
  scheduledOn: number
  callDate: number | null
  reportedBy: string | null
  reportedByGroup: UserGroup | null
  reportedOn: number | null
}
export type PregnancyOutcome =
  | 'born-living'
  | 'born-with-complications'
  | 'miscarriage'
  | 'abortion'
  | 'other'

export interface ChildsGestationAndBirth {
  problemsDuringPregnancy?: string | null
  problemsDuringPregnancyDetails?: string | null
  treatmentsDuringPregnancy?: string | null
  treatmentsDuringPregnancyDetails?: string | null
  drugOrAlcoholUseDuringPregnancy?: string | null
  drugOrAlcoholUseDuringPregnancyDetails?: string | null
  complicationsDuringBirth?: string | null
  complicationsDuringBirthDetails?: string | null
  complicationsDuringInfancy?: string | null
  complicationsDuringInfancyDetails?: string | null
}

export interface Assessment extends Partial<DBMetadata> {
  name?: string

  data?: WithPartialMetadata<FieldMapValue>
  corrections?: WithPartialMetadata<FieldMapValue>
  primaryCoverageId: PatientCoverageId | null
  secondaryCoverageId: PatientCoverageId | null
  potentialCoverageIds?: PatientCoverageId[]
  // TODO: remove
  // additionalPlans?: Record<string, InsuranceCoverage | InsuranceCoverageRequest>

  additionalPlansConfirmedOn?: number
  additionalPlansConfirmedBy?: string
  additionalPlansRequestedOn?: number
  additionalPlansRequestedBy?: string

  skippedQuestionnaire?: boolean
  questionnaireSkippedOn?: number | null
  questionnaireSkippedBy?: string | null
  delivery: WithMetadata<Partial<Delivery>> | null

  createdOn: number
  createdBy?: string
  summarySubmittedOn?: number | null
  summarySubmittedBy?: string | null
  summarySubmittedByGroup?: UserGroup | null
  scheduleCallNotificationSentOn?: number | null
  scheduleCallNotificationSentBy?: string | null
  scheduleCallNotificationSentByGroup?: UserGroup | null
  insurerCallStatus?: 'opted-in' | 'opted-out' | null
  insurerCallInAlertDismissedOn?: ServerTimestamp | null
  insurerCallInAlertDismissedBy?: string | null
  // insurerCallInUpdatedOn?: ServerTimestamp | null
  insurerCallInSubmittedOn?: ServerTimestamp | null
  insurerCallInSubmittedBy?: string | null

  submittedOn?: number
  submittedBy?: string
  submittedByGroup?: 'patient' | 'practice' | 'admin'
  sentOn?: number
  sentBy?: string
  answersUpdatedOn?: number
  answersUpdatedBy?: string
  answersUpdatedByGroup?: UserGroup
  correctionsUpdatedOn?: number
  // TODO: delete (moved to snippet)
  status?: AssessmentStatus

  signOnData?: WithPartialMetadata<FieldMapValue>
  signOnCorrections?: WithPartialMetadata<FieldMapValue>
  signedOnDate?: number | null
  signOnSkippedBy?: string | null
  signOnSkippedOn?: number | null
  signOnReminders?: { sentOn: number }[]
  disclaimers?: {
    disclaimer1Accepted?: boolean
    disclaimer2Accepted?: boolean
  }
  scheduledCall?: ScheduledCall | null

  correctionsUpdatedBy?: string
  correctionsUpdatedByGroup?: UserGroup
  resultsViewedOn?: number
  viewableBeforeCall?: boolean
  patientId: string
  results?: Descendant[]
  resultsInsurerId?: string | null
  requiredConsentFormIds?: string[]

  // if no version specified, assume v1
  editorVersion?: EditorVersion
  previousResults?: {
    insuranceProviderId?: string | null
    editorVersion?: EditorVersion
    results: Descendant[]
    sentOn: number
    sentBy: string
  }[]

  authAppeals?: Record<string, AssessmentDocument>
  authInstructions?: Record<string, AssessmentDocument>

  archivedOn: number | null
  archivedBy?: string | null
  archivedByGroup?: UserGroup | null
  archivedHistory?: ArchivedHistoryItem[]
  midwifeId?: string | null

  files?: Record<string, FileDBValue>

  inviteSentOn?: number
  inviteSentBy?: string

  forceUpdatedOn?: number
  forceUpdateOn?: number

  miscarriedUpdatedOn?: number | null
  miscarriedUpdatedBy?: string | null
  miscarriedUpdatedByGroup?: 'patient' | 'practice' | 'admin' | null
  miscarriedHistory?: MiscarriedHistoryItem[]
  miscarried?: boolean

  laborCharting?: LaborChartingData

  questionnaireNote?: Note | null

  // pregnancyOutcome?: PregnancyOutcome | null
  // pregnancyOutcomeOther?: string | null

  // childsGestationAndBirth?: ChildsGestationAndBirth | null
}

export type NewAssessment = Partial<Assessment>
export const isNewAssessment = (a: Assessment | NewAssessment): a is NewAssessment => !a.submittedOn

export type AssessmentSortKey =
  | keyof Pick<
      AssessmentSnippet,
      | 'email'
      | 'lname'
      | 'nextActionDate'
      | 'insuranceCoverage'
      | 'deliveredOn'
      | 'signedOnDate'
      | 'midwifeName'
      | 'inquiryRank'
      | 'hasOverduePayment'
      | 'redFlagText'
      | 'edd'
      | 'unreadThreadMessages'
    >
  | 'claimNextAction'
  | keyof Pick<PracticeAssessmentData, 'sortColor' | 'nextPatientAppointmentDate'>

export type ConfirmCoverageStage = 'primaryCoverage' | 'secondaryCoverage' | string | 'confirm'
export type CoverageSnippet = 'basic-info' | 'call-in' | 'policy-owner'
type CoverageStageIncomplete = CoverageSnippet[]
type CoverageStageRequired = CoverageSnippet[]
export type CoverageStageStatus = {
  incomplete: CoverageStageIncomplete
  required: CoverageStageRequired
} | null

export interface ConfirmCoverageStatus {
  primaryCoverage: CoverageStageStatus
  secondaryCoverage: CoverageStageStatus
  // additionalPlans: Record<AdditionalCoverageId, CoverageStageStatus>
  // requests: Record<string, CoverageStageStatus>
}

export type ListNextActionEntity =
  | { type: 'gyn' }
  | {
      type: 'coverage'
      id: PatientCoverageId
      coverage: InsuranceCoverage | PregnancyCoverage<InsuranceCoverage> | undefined | null
    }

export interface PregnancyInsurancePlans<T extends BaseInsuranceCoverage = CoverageWithMetadata> {
  // nonMedicaid: T | null
  // medicaid: T | null
  primary: T | null
  secondary: T | null
  potential: Array<T>
}

export type PopulatedPregnancyPlan = PregnancyCoverage<InsuranceCoverage>

export interface PregnancyCoverageNextAction {
  coverage: PopulatedPregnancyPlan | null | undefined
  id: PatientCoverageId
  date: number
  nextAction: NextAction | null | undefined
  backupNextAction: NextAction | null | undefined
}

export const isPopulatedPregnancyPlan = (
  plan: PopulatedPregnancyPlan | InsuranceCoverage | BaseInsuranceCoverage,
): plan is PopulatedPregnancyPlan => (plan as PopulatedPregnancyPlan).pregnancyLabel !== undefined

export type PopulatedPregnancyPlans = Required<PregnancyInsurancePlans<PopulatedPregnancyPlan>>
